// components/Pricing.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";

const Pricing = () => {
    const [billingType, setBillingType] = useState("monthly");

    const pricingOptions = {
        monthly: {
            label: "Monthly Payment Plan",
            badge: "Flexible Development Model",
            title: "Structured Monthly Development",
            price: "Scope-Based Quote",
            subtitle: "Paid monthly by the 10th",
            description:
                "A professional monthly development model for clients who need phased delivery, continuous progress, and a reliable technical partner throughout the project. The first monthly payment secures the project start and must be paid before development begins.",
            highlights: [
                "First monthly payment is paid upfront as the project commencement deposit",
                "Subsequent payments are due by the 10th of every month",
                "Development is delivered in structured phases based on the approved scope",
                "Ideal for full-stack systems, dashboards, mobile apps, SaaS platforms, and long-term digital products",
            ],
            paymentTerms: [
                {
                    label: "Commencement Deposit",
                    value: "First monthly payment before development begins",
                },
                {
                    label: "Monthly Payment",
                    value: "Paid by the 10th of every month",
                },
                {
                    label: "Delivery Timeline",
                    value: "Defined by the agreed scope, milestones, and deliverables",
                },
                {
                    label: "Final Investment",
                    value: "Confirmed after scope review and technical assessment",
                },
            ],
        },

        full: {
            label: "One-Time Full Payment",
            badge: "Premium Fixed-Scope Model",
            title: "Complete Project Payment",
            price: "Scope-Based Quote",
            subtitle: "50% deposit to begin",
            description:
                "A professional project payment model for clients who prefer a clear, milestone-based structure with fewer payment stages. A 50% deposit secures the project start, while the remaining balance is paid upon completion or at the agreed delivery milestone.",
            highlights: [
                "50% deposit is required before development begins",
                "Remaining 50% is paid upon completion or the agreed delivery milestone",
                "Best for clearly scoped websites, systems, dashboards, mobile apps, and digital platforms",
                "Final quote and delivery timeline are confirmed after reviewing the complete scope of work",
            ],
            paymentTerms: [
                {
                    label: "Project Deposit",
                    value: "50% of total project cost before development begins",
                },
                {
                    label: "Completion Balance",
                    value: "50% upon completion or agreed delivery milestone",
                },
                {
                    label: "Delivery Timeline",
                    value: "Defined by the approved project scope and deliverables",
                },
                {
                    label: "Final Investment",
                    value: "Confirmed after scope review and technical assessment",
                },
            ],
        },
    };

    const includedServices = [
        "Requirement analysis and project planning",
        "UI/UX structure and frontend development",
        "Backend API development where required",
        "Database design and integration",
        "Authentication and authorization where required",
        "Payment integration where required",
        "Testing, debugging, and deployment support",
        "Basic documentation and handover guidance",
    ];

    const scopeFactors = [
        "Number of pages, screens, or modules",
        "Frontend-only vs full-stack development",
        "Admin dashboard requirements",
        "Authentication and user roles",
        "Payment integrations such as PayPal, M-Pesa, or Stripe",
        "Database complexity",
        "Hosting, deployment, and maintenance needs",
        "Third-party API integrations",
        "Mobile app or web app requirements",
    ];

    const monthlySupportPlans = [
        {
            title: "Comprehensive Technical Maintenance",
            price: "KES 50,000",
            period: "per month",
            badge: "Premium Post-Launch Support",
            description:
                "A premium monthly technical retainer for businesses that need their website, system, or application kept stable, secure, updated, monitored, and continuously improved after launch.",
            features: [
                "Up to 12 monthly technical support hours",
                "Priority bug fixes and minor improvements",
                "Security monitoring and basic hardening",
                "Monthly backups and recovery checks",
                "Performance checks and optimization",
                "Content, layout, and small UI updates",
                "Dependency and package update reviews",
                "Uptime, error, and stability monitoring guidance",
                "Monthly technical health report",
                "Recommendations for future improvements",
            ],
            note: "Best for businesses with a live website, system, dashboard, or application that requires serious ongoing technical care.",
            icon: "bx bx-shield-quarter",
        },
        {
            title: "Strategic Technology Consultation",
            price: "KES 60,000",
            period: "per month",
            badge: "Executive Tech Advisory",
            description:
                "A premium monthly advisory plan for founders, SMEs, startups, and teams that need consistent technical direction, system planning, architecture guidance, product reviews, and business technology strategy.",
            features: [
                "Up to 8 monthly consultation/advisory hours",
                "Technical project consultation and planning",
                "Scope review and feature prioritization",
                "System architecture and scalability guidance",
                "Code review and improvement recommendations",
                "Deployment, hosting, and infrastructure guidance",
                "Cybersecurity and risk advisory",
                "Business technology strategy sessions",
                "Monthly progress and roadmap review",
                "Decision support before major tech investments",
            ],
            note: "Best for founders, SMEs, startups, and teams that need senior-level technical guidance before, during, or after development.",
            icon: "bx bx-brain",
        },
    ];

    const selectedPlan = pricingOptions[billingType];

    return (
        <section
            id="pricing"
            className="section bg-[#f4f3ee] py-16 dark:bg-black"
        >
            <div className="container mx-auto px-6">
                <div className="mx-auto mb-12 max-w-3xl text-center">
                    <h2 className="section-title text-gray-900 dark:text-white">
                        Flexible <span className="brand-accent">Pricing</span>{" "}
                        Based on <span className="brand-accent-green">Scope</span>
                    </h2>

                    <p className="mt-4 text-lg leading-snug tracking-[-0.025em] text-gray-600 dark:text-gray-400">
                        Every project is quoted based on its size, complexity, timeline, and
                        deliverables. Choose between monthly payments or a one-time project
                        payment structure.
                    </p>
                </div>

                <div className="mx-auto mb-12 flex max-w-xl rounded-full border border-black/10 bg-[#fbfaf6] p-1 dark:border-white/10 dark:bg-[#131416]">
                    <button
                        type="button"
                        onClick={() => setBillingType("monthly")}
                        className={`flex-1 rounded-full px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-all duration-300 sm:text-sm ${billingType === "monthly"
                                ? "brand-button"
                                : "text-gray-700 hover:text-black dark:text-gray-300 dark:hover:text-white"
                            }`}
                    >
                        Monthly Payment Plan
                    </button>

                    <button
                        type="button"
                        onClick={() => setBillingType("full")}
                        className={`flex-1 rounded-full px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-all duration-300 sm:text-sm ${billingType === "full"
                                ? "brand-button"
                                : "text-gray-700 hover:text-black dark:text-gray-300 dark:hover:text-white"
                            }`}
                    >
                        Full Payment Plan
                    </button>
                </div>

                <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.05fr_0.95fr]">
                    <div className="brand-card-accent brand-soft-panel rounded-lg border p-8">
                        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <h3 className="text-4xl font-normal leading-[0.95] tracking-[-0.06em] text-gray-900 dark:text-white">
                                    {selectedPlan.title}
                                </h3>

                                <p className="mt-2 text-gray-600 dark:text-gray-400">
                                    {selectedPlan.subtitle}
                                </p>
                            </div>

                            <div className="rounded-lg border border-black/10 bg-[#f4f3ee] px-5 py-4 text-left dark:border-white/10 dark:bg-black sm:text-right">
                                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                                    Starting From
                                </p>

                                <p className="mt-1 text-2xl font-normal tracking-[-0.04em] text-gray-900 dark:text-white">
                                    {selectedPlan.price}
                                </p>
                            </div>
                        </div>

                        <p className="mb-8 leading-snug tracking-[-0.02em] text-gray-700 dark:text-gray-300">
                            {selectedPlan.description}
                        </p>

                        <div className="mb-8 grid gap-4 sm:grid-cols-2">
                            {selectedPlan.paymentTerms.map((term, index) => (
                                <div
                                    key={index}
                                    className="rounded-lg border border-black/10 bg-[#f4f3ee] p-5 dark:border-white/10 dark:bg-black/30"
                                >
                                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-900 dark:text-white">
                                        {term.label}
                                    </p>

                                    <p className="mt-2 text-gray-800 dark:text-gray-200">
                                        {term.value}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="space-y-4">
                            {selectedPlan.highlights.map((item, index) => (
                                <div key={index} className="flex gap-3">
                                    <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-black/10 text-gray-900 dark:border-white/10 dark:text-white">
                                        <i className="bx bx-check text-lg"></i>
                                    </div>

                                    <p className="text-gray-700 dark:text-gray-300">{item}</p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                            <Link
                                to="/contact"
                                className="brand-button inline-flex items-center justify-center rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] transition-all duration-300"
                            >
                                Request a Quote
                                <i className="bx bx-right-arrow-alt ml-2 text-xl"></i>
                            </Link>

                            <Link
                                to="/about"
                                className="brand-button-outline inline-flex items-center justify-center rounded-full border px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] transition-all duration-300"
                            >
                                View Projects
                            </Link>
                        </div>
                    </div>

                    <div className="space-y-8">
                        <div className="brand-card-accent rounded-lg border bg-[#fbfaf6] p-8 dark:bg-[#131416]">
                            <h3 className="text-3xl font-normal leading-none tracking-[-0.05em] text-gray-900 dark:text-white">
                                What Affects the Quote?
                            </h3>

                            <p className="mt-3 text-gray-600 dark:text-gray-400">
                                The final cost is calculated after reviewing the full scope of
                                work.
                            </p>

                            <div className="mt-6 space-y-3">
                                {scopeFactors.map((factor, index) => (
                                    <div key={index} className="flex items-start gap-3">
                                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-900 dark:bg-white"></span>

                                        <p className="text-gray-700 dark:text-gray-300">
                                            {factor}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="brand-card-accent rounded-lg border bg-[#fbfaf6] p-8 dark:bg-[#131416]">
                            <h3 className="text-3xl font-normal leading-none tracking-[-0.05em] text-gray-900 dark:text-white">
                                What Is Included?
                            </h3>

                            <div className="mt-6 space-y-3">
                                {includedServices.map((service, index) => (
                                    <div key={index} className="flex items-start gap-3">
                                        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-black/10 text-gray-900 dark:border-white/10 dark:text-white">
                                            <i className="bx bx-check"></i>
                                        </div>

                                        <p className="text-gray-700 dark:text-gray-300">
                                            {service}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="brand-card-accent mx-auto mt-10 max-w-6xl rounded-lg border bg-[#fbfaf6] p-6 dark:bg-[#131416]">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-black/10 text-gray-900 dark:border-white/10 dark:text-white">
                            <i className="bx bx-info-circle text-2xl"></i>
                        </div>

                        <div>
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Payment Terms Notice
                            </h4>

                            <p className="mt-2 leading-relaxed text-gray-700 dark:text-gray-300">
                                Work begins only after the agreed deposit is paid. For monthly
                                projects, the first monthly payment serves as the deposit. For
                                one-time projects, 50% of the total quoted amount is required to
                                begin development. The duration and final price depend on the
                                approved scope of work.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mx-auto mt-10 max-w-6xl">
                    <div className="mb-8 text-center">
                        <h3 className="text-4xl font-normal leading-none tracking-[-0.06em] text-gray-900 dark:text-white">
                            Ongoing <span className="brand-accent">Support</span> & Advisory Plans
                        </h3>

                        <p className="mx-auto mt-3 max-w-2xl text-gray-600 dark:text-gray-400">
                            These plans are separate from project development costs and are
                            designed for clients who need continuous support, maintenance, or
                            technical guidance every month.
                        </p>
                    </div>

                    <div className="grid gap-8 lg:grid-cols-2">
                        {monthlySupportPlans.map((plan, index) => (
                            <div
                                key={index}
                                className="brand-card-accent relative overflow-hidden rounded-lg border bg-[#fbfaf6] p-8 transition-all duration-300 hover:-translate-y-1 dark:bg-[#131416]"
                            >
                                <div className="relative z-10">
                                    <div className="mb-6 flex items-start justify-between gap-4">
                                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-black/10 bg-[var(--brand-blue)] text-white dark:border-white/10">
                                            <i className={`${plan.icon} text-3xl`}></i>
                                        </div>

                                        <span className="rounded-full border border-black/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-gray-700 dark:border-white/10 dark:text-gray-300">
                                            {plan.badge}
                                        </span>
                                    </div>

                                    <h4 className="text-3xl font-normal leading-none tracking-[-0.05em] text-gray-900 dark:text-white">
                                        {plan.title}
                                    </h4>

                                    <p className="mt-3 leading-relaxed text-gray-600 dark:text-gray-400">
                                        {plan.description}
                                    </p>

                                    <div className="mt-6 rounded-lg border border-black/10 bg-[#f4f3ee] p-5 dark:border-white/10 dark:bg-black/30">
                                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                                            Starting From
                                        </p>

                                        <div className="mt-1 flex items-end gap-2">
                                            <span className="text-4xl font-normal tracking-[-0.06em] text-gray-900 dark:text-white">
                                                {plan.price}
                                            </span>

                                            <span className="pb-1 text-gray-500 dark:text-gray-400">
                                                {plan.period}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-6 space-y-3">
                                        {plan.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex gap-3">
                                                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-black/10 text-gray-900 dark:border-white/10 dark:text-white">
                                                    <i className="bx bx-check"></i>
                                                </div>

                                                <p className="text-gray-700 dark:text-gray-300">
                                                    {feature}
                                                </p>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-6 rounded-lg border border-black/10 bg-[#f4f3ee] p-4 dark:border-white/10 dark:bg-black/30">
                                        <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                                            {plan.note}
                                        </p>
                                    </div>

                                    <Link
                                        to="/contact"
                                        className="brand-button-green mt-6 inline-flex w-full items-center justify-center rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] transition-all duration-300"
                                    >
                                        Discuss This Plan
                                        <i className="bx bx-right-arrow-alt ml-2 text-xl"></i>
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="brand-ink-gradient mx-auto mt-12 max-w-4xl rounded-lg p-8 text-center text-white">
                    <h3 className="text-4xl font-normal leading-none tracking-[-0.06em]">Have a Project in Mind?</h3>

                    <p className="mx-auto mt-3 max-w-2xl text-[#d7d1c4]">
                        Share your scope, timeline, and expected deliverables so I can
                        prepare a realistic quote and development plan.
                    </p>

                    <div className="mt-6 flex flex-col justify-center gap-4 sm:flex-row">
                        <Link
                            to="/contact"
                            className="rounded-full bg-white px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-black transition-all duration-300 hover:bg-[#d7d1c4]"
                        >
                            Start Scope Review
                        </Link>

                        <Link
                            to="/about"
                            className="rounded-full border border-white/40 px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:border-white hover:bg-white hover:text-black"
                        >
                            View Services
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Pricing;
